import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResult } from '../../../../shared/models/api-result.model';
import {
  Carrier,
  CreateCarrier,
  UpdateCarrier,
} from '../../../models/carrier.model';
import { CarrierService } from '../../../services/carrier.service';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';

@Component({
  selector: 'app-carrier-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ToolbarModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    ToastModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
  ],
  templateUrl: './carrier-create-or-update.component.html',
})
export class CarrierCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private carrierService = inject(CarrierService);
  private formGroupService = inject(FormGroupService);

  carrierId = signal<string | null>(null);
  loading = signal(false);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    websiteUrl: [null, [Validators.maxLength(255)]],
    trackingUrl: [null, [Validators.maxLength(255)]],
    isActive: [true],
  });

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');

    if (id) {
      this.carrierId.set(id);
      this.loadData();
    }
  }

  loadData(): void {
    const id = this.carrierId();
    if (!id) return;

    this.loading.set(true);

    this.carrierService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as Carrier;
        this.formGroup.patchValue(model);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a transportadora',
            life: 3000,
          });
        }
      },
    });
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.get(key)?.markAsTouched();
      });
      return;
    }

    const id = this.carrierId();
    const body = this.formGroup.getRawValue();

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    if (id) {
      request$ = this.carrierService.update(id, body as UpdateCarrier);
    } else {
      delete (body as any).id;
      delete (body as any).isActive;
      request$ = this.carrierService.create(body as CreateCarrier);
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Transportadora salva com sucesso',
          life: 2000,
        });

        this.router.navigate(['/carriers']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors?.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a transportadora',
              life: 3000,
            });
          }
        }
      },
    });
  }
}
